/**
 * Lire les heures dans une conversation WhatsApp.
 *
 * Depuis le 31 août, les équipes écrivent leurs heures le soir sur le groupe.
 * Elles les écrivent comme elles parlent : « Pierrerue mercredi 9 septembre
 * Joaquim,Justin ,Arnoux 7h 15h45 avec pause 30m », « Cyr Jim: 6h30 h 15h45 »,
 * « Vendredi Justin 7h 14h Joaquim + Arnoux 7h 14h 30 sans pause ». Recopier
 * ça à la main tous les mois, c'est une heure de travail et deux erreurs.
 *
 * Ce module lit le texte exporté et en sort des relevés. Il ne décide rien : il
 * PROPOSE, signale tout ce dont il n'est pas sûr, et laisse la personne
 * corriger avant d'enregistrer. Trois règles le gouvernent :
 *
 *  1. Rien n'est inventé. Un horaire qu'on n'a pas su lire ne devient pas
 *     14 h 30 : il devient une anomalie, et la ligne reste à compléter.
 *  2. La date retenue est celle de l'ENVOI, pas celle écrite dans le message.
 *     Sur les vraies données, quand les deux divergent c'est le titre qui est
 *     faux (« Jeudi 16 septembre » envoyé le 17). La date écrite est conservée
 *     et l'écart signalé, pour qu'on puisse trancher dans l'autre sens.
 *  3. Un nom qu'on ne reconnaît pas n'est pas jeté. La ligne existe, sans
 *     employé rattaché, et attend qu'on lui en désigne un.
 */

import {
  parseHeureJour,
  parsePauseMinutes,
  type Minutes,
  type MinutesJour,
} from './releve-heures.js';

export type CodeAnomalie =
  /** Une ligne parle d'heures mais on n'a pas su en lire deux. */
  | 'horaire_illisible'
  /** Des heures sans personne à qui les attribuer. */
  | 'nom_inconnu'
  /** Un prénom abrégé qui désigne plusieurs employés. */
  | 'nom_ambigu'
  /** Le message annonce une autre date que celle de son envoi. */
  | 'date_ecrite_differente'
  /** « avec pause », sans dire combien. */
  | 'pause_sans_duree'
  /** La même personne deux fois le même jour dans le même import. */
  | 'doublon_jour';

export interface Anomalie {
  code: CodeAnomalie;
  message: string;
  /** Le texte d'origine, pour pouvoir vérifier sans rouvrir WhatsApp. */
  source: string;
}

export interface LigneImportee {
  /** La date retenue, `AAAA-MM-JJ`. */
  jour: string;
  /** La date annoncée dans le message, si elle diffère de celle retenue. */
  jourEcrit: string | null;
  auteur: string;
  equipe: string;
  /** Le nom tel qu'il est écrit dans le message. */
  nom: string;
  /** L'employé reconnu, ou `null` s'il reste à désigner. */
  employeId: string | null;
  debut: MinutesJour;
  fin: MinutesJour;
  pauseDeclaree: Minutes | null;
  chantierLibelle: string | null;
  source: string;
  anomalies: Anomalie[];
}

export interface ResultatImport {
  lignes: LigneImportee[];
  /** Les auteurs qui ont déclaré des heures, pour leur affecter une équipe. */
  auteurs: string[];
  /** Anomalies ne portant sur aucune ligne (horaire illisible, nom inconnu). */
  anomalies: Anomalie[];
  /**
   * Les mots croisés à côté d'un nom connu sans être reconnus eux-mêmes.
   *
   * C'est ainsi qu'on retrouve « Arnoux », écrit pour Arnaud : sans cette
   * liste il disparaîtrait de l'import sans un mot. Elle contient aussi des
   * noms de chantiers — c'est le prix à payer pour ne rien perdre.
   */
  nomsNonReconnus: { mot: string; occurrences: number }[];
  /** Messages lus mais sans heures dedans. Sert à dire « 412 messages, 79 relevés ». */
  messagesSansHeures: number;
}

export interface Salarie {
  employeId: string;
  /** Le prénom usuel. C'est lui qui est comparé. */
  prenom: string;
  /** Les autres façons de l'écrire : « Arnoux » pour Arnaud, « Jo » pour Joaquim. */
  alias: readonly string[];
}

export interface OptionsImport {
  salaries: readonly Salarie[];
  /** Quel auteur poste pour quelle équipe. À défaut, le premier mot de son nom. */
  equipeParAuteur: Readonly<Record<string, string>>;
  /** Ne garder que les messages envoyés à partir de ce jour (`AAAA-MM-JJ`). */
  depuis: string | null;
  /** Ne garder que les messages envoyés jusqu'à ce jour inclus. */
  jusqua: string | null;
}

/** Garde-fou : au-delà, c'est un collage accidentel de tout l'historique. */
const LIGNES_MAX = 20000;

/**
 * À quoi reconnaît-on un relevé d'une phrase ordinaire ?
 *
 * « Jimmy ne pourra pas être là à 8h, il arrive vers 9h » contient un prénom et
 * deux heures : pour un automate, c'est une journée de travail de 8 h à 9 h.
 * Trois bornes suffisent à l'écarter sans perdre un vrai relevé — les plus
 * courtes journées du groupe font six heures et demie, et une déclaration
 * d'heures tient en une douzaine de mots.
 */
const DUREE_MIN = 90;
const DUREE_MAX = 16 * 60;
const DEBUT_MIN = 4 * 60;
const DEBUT_MAX = 13 * 60;
const MOTS_MAX = 18;

function ressembleAUnReleve(ligne: string, debut: MinutesJour, fin: MinutesJour): boolean {
  const duree = fin - debut;
  if (duree < DUREE_MIN || duree > DUREE_MAX) return false;
  if (debut < DEBUT_MIN || debut > DEBUT_MAX) return false;
  const mots = ligne
    .replace(HEURE, ' ')
    .split(/[^\p{L}\p{N}]+/u)
    .filter((m) => m !== '');
  return mots.length <= MOTS_MAX;
}

// ----- Repérage des heures --------------------------------------------------

/**
 * Les quatre façons d'écrire une heure, essayées de la plus précise à la moins
 * précise. L'ordre compte : sur « 7h 15h30 », la deuxième forme doit échouer
 * pour que la première heure reste 7 h et non 7 h 15.
 */
const HEURE = new RegExp(
  [
    // 16h15, 16 h15, 6h30 — minutes collées au « h ». Pas si un « h » suit :
    // « 7 h17h » est un 7 h suivi d'un 17 h, pas un 7 h 17.
    String.raw`(\d{1,2})\s*[h:](\d{2})(?![h:])`,
    // 7h 30 — minutes détachées, acceptées seulement si rien derrière ne
    // ressemble à une heure : « 7 h 16 h15 » vaut 7 h, pas 7 h 16.
    String.raw`(\d{1,2})\s*[h:]\s+(\d{2})(?!\s*[h:.,])(?!\d)`,
    // 15.30, 14 ,30 — le séparateur décimal des feuilles d'heures papier.
    String.raw`(\d{1,2})\s*[.,]\s*(\d{2})`,
    // 7h, 7 h — heure pleine.
    String.raw`(\d{1,2})\s*[h:]`,
  ].join('|'),
  'g',
);

interface HeureTrouvee {
  minutes: MinutesJour;
  debut: number;
  fin: number;
}

function heuresDeLaLigne(ligne: string): HeureTrouvee[] {
  const trouvees: HeureTrouvee[] = [];
  HEURE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = HEURE.exec(ligne)) !== null) {
    if (m[0].length === 0) {
      HEURE.lastIndex += 1;
      continue;
    }
    const h = m[1] ?? m[3] ?? m[5] ?? m[7];
    const min = m[2] ?? m[4] ?? m[6];
    if (h === undefined) continue;
    const minutes = parseHeureJour(min === undefined ? `${h}h` : `${h}h${min}`);
    if (minutes === null) continue;
    trouvees.push({ minutes, debut: m.index, fin: m.index + m[0].length });
  }
  return trouvees;
}

// ----- Repérage des noms ----------------------------------------------------

function normaliser(texte: string): string {
  return texte.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** Un mot est un nom s'il vaut un alias, ou s'il abrège un prénom (« Cyr »). */
function reconnaitre(
  mot: string,
  salaries: readonly Salarie[],
): { salarie: Salarie | null; ambigu: boolean } {
  const m = normaliser(mot);
  if (m.length < 3) return { salarie: null, ambigu: false };

  const exacts = salaries.filter(
    (s) => normaliser(s.prenom) === m || s.alias.some((a) => normaliser(a) === m),
  );
  if (exacts.length === 1) return { salarie: exacts[0] ?? null, ambigu: false };
  if (exacts.length > 1) return { salarie: null, ambigu: true };

  const prefixes = salaries.filter(
    (s) => normaliser(s.prenom).startsWith(m) || s.alias.some((a) => normaliser(a).startsWith(m)),
  );
  if (prefixes.length === 1) return { salarie: prefixes[0] ?? null, ambigu: false };
  if (prefixes.length > 1) return { salarie: null, ambigu: true };
  return { salarie: null, ambigu: false };
}

interface NomTrouve {
  mot: string;
  salarie: Salarie | null;
  ambigu: boolean;
}

/** Les noms d'une tranche de texte, dans l'ordre, sans doublon. */
function nomsDuSegment(segment: string, salaries: readonly Salarie[]): NomTrouve[] {
  const trouves: NomTrouve[] = [];
  const vus = new Set<string>();
  for (const mot of segment.split(/[^\p{L}]+/u)) {
    if (mot === '') continue;
    const { salarie, ambigu } = reconnaitre(mot, salaries);
    if (salarie === null && !ambigu) continue;
    const cle = salarie?.employeId ?? `?${normaliser(mot)}`;
    if (vus.has(cle)) continue;
    vus.add(cle);
    trouves.push({ mot, salarie, ambigu });
  }
  return trouves;
}

// ----- Dates ----------------------------------------------------------------

const MOIS = [
  'janvier',
  'fevrier',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'aout',
  'septembre',
  'octobre',
  'novembre',
  'decembre',
];

function moisDuMot(mot: string): number | null {
  const m = normaliser(mot);
  if (m.length < 3) return null;
  const i = MOIS.findIndex((nom) => nom.startsWith(m));
  return i === -1 ? null : i + 1;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** La date écrite dans le message (« 31 août », « 3septembre »), s'il y en a une. */
function dateEcrite(texte: string, anneeEnvoi: number, moisEnvoi: number): string | null {
  const re = /(\d{1,2})\s*(?:de\s+)?([\p{L}]{3,10})/gu;
  let m: RegExpExecArray | null;
  while ((m = re.exec(texte)) !== null) {
    const jour = Number(m[1]);
    const mois = moisDuMot(m[2] ?? '');
    if (mois === null || jour < 1 || jour > 31) continue;
    // Un message de janvier qui parle de décembre parle de l'année d'avant.
    let annee = anneeEnvoi;
    if (mois === 12 && moisEnvoi === 1) annee -= 1;
    else if (mois === 1 && moisEnvoi === 12) annee += 1;
    return `${String(annee)}-${pad(mois)}-${pad(jour)}`;
  }
  return null;
}

// ----- Découpage de l'export ------------------------------------------------

const ENTETE = /^‎?\[([^\]]+)\]\s*(.*)$/;

interface Message {
  jourEnvoi: string;
  auteur: string;
  lignes: string[];
}

/**
 * Dans quel sens sont écrites les dates de l'export ?
 *
 * WhatsApp exporte en `M/J/AA` ou en `J/M/AAAA` selon le téléphone. Lire
 * `9/11/26` à l'envers décale tout un mois de relevés sans que rien ne le
 * signale, alors on tranche sur l'ensemble du fichier : un nombre supérieur à
 * 12 à une position donne le sens pour tout le reste.
 */
export function detecterOrdreDate(lignes: readonly string[]): 'jour_mois' | 'mois_jour' {
  let ampm = false;
  for (const ligne of lignes) {
    const m = ENTETE.exec(ligne);
    if (!m) continue;
    const brut = m[1] ?? '';
    if (/\b[ap]\.?m\.?\b/i.test(brut)) ampm = true;
    const d = /(\d{1,4})[/.-](\d{1,4})[/.-](\d{2,4})/.exec(brut);
    if (!d) continue;
    const a = Number(d[1]);
    const b = Number(d[2]);
    if (a > 12) return 'jour_mois';
    if (b > 12) return 'mois_jour';
  }
  // Aucun nombre n'a tranché : l'heure en AM/PM trahit un export américain.
  return ampm ? 'mois_jour' : 'jour_mois';
}

function jourDeLEntete(brut: string, ordre: 'jour_mois' | 'mois_jour'): string | null {
  const d = /(\d{1,4})[/.-](\d{1,4})[/.-](\d{2,4})/.exec(brut);
  if (!d) return null;
  const a = Number(d[1]);
  const b = Number(d[2]);
  let annee = Number(d[3]);
  if (annee < 100) annee += 2000;
  const jour = ordre === 'jour_mois' ? a : b;
  const mois = ordre === 'jour_mois' ? b : a;
  if (jour < 1 || jour > 31 || mois < 1 || mois > 12) return null;
  return `${String(annee)}-${pad(mois)}-${pad(jour)}`;
}

const PIECE_JOINTE =
  /^(<[^>]*>|‎?(image|vidéo|video|audio|sticker|gif|document|contact)[^:]*omis[e]?|‎?document manquant|‎?<pièce jointe[^>]*>)$/i;

function decouper(texte: string): { messages: Message[]; ordre: 'jour_mois' | 'mois_jour' } {
  const lignes = texte.replace(/\r\n?/g, '\n').split('\n').slice(0, LIGNES_MAX);
  const ordre = detecterOrdreDate(lignes);
  const messages: Message[] = [];

  for (const ligneBrute of lignes) {
    const ligne = ligneBrute.replace(/‎/g, '').trimEnd();
    const entete = ENTETE.exec(ligne);
    if (entete) {
      const jour = jourDeLEntete(entete[1] ?? '', ordre);
      const reste = entete[2] ?? '';
      const sep = reste.indexOf(':');
      // Pas de « Auteur: » → message système (« X a rejoint le groupe »).
      if (jour === null || sep === -1) {
        messages.push({ jourEnvoi: jour ?? '', auteur: '', lignes: [] });
        continue;
      }
      messages.push({
        jourEnvoi: jour,
        auteur: reste.slice(0, sep).trim(),
        lignes: [reste.slice(sep + 1).trim()],
      });
      continue;
    }
    const dernier = messages[messages.length - 1];
    if (dernier && dernier.auteur !== '') dernier.lignes.push(ligne.trim());
  }

  for (const msg of messages) {
    msg.lignes = msg.lignes.filter((l) => l !== '' && !PIECE_JOINTE.test(l));
  }
  return { messages: messages.filter((m) => m.auteur !== '' && m.lignes.length > 0), ordre };
}

// ----- Libellé du chantier --------------------------------------------------

const MOTS_IGNORES = new Set([
  'lundi',
  'mardi',
  'mercredi',
  'jeudi',
  'vendredi',
  'samedi',
  'dimanche',
  'avec',
  'sans',
  'pause',
  'de',
  'du',
  'le',
  'la',
  'et',
  'a',
  'e',
  'h',
  'min',
  'mn',
  'm',
]);

/** Ce qui reste d'une ligne une fois ôtés les heures, les noms et les dates. */
function libelleDeLaLigne(ligne: string, salaries: readonly Salarie[]): string | null {
  let reste = ligne;
  for (const h of [...heuresDeLaLigne(ligne)].reverse()) {
    reste = reste.slice(0, h.debut) + ' ' + reste.slice(h.fin);
  }
  // Découpage sur les lettres seules : « 2septembre » doit rendre « septembre »
  // pour être reconnu comme un mois, et « 30m » rendre « m », un mot outil.
  const mots = reste
    .split(/[^\p{L}]+/u)
    .filter((mot) => mot !== '')
    .filter((mot) => moisDuMot(mot) === null)
    .filter((mot) => !MOTS_IGNORES.has(normaliser(mot)))
    .filter((mot) => reconnaitre(mot, salaries).salarie === null);
  const libelle = mots.join(' ').trim();
  return libelle.length >= 2 && libelle.length <= 48 ? libelle : null;
}

// ----- Lecture --------------------------------------------------------------

export function lireHeuresWhatsApp(
  texte: string,
  options: Partial<OptionsImport> = {},
): ResultatImport {
  const salaries = options.salaries ?? [];
  const equipeParAuteur = options.equipeParAuteur ?? {};
  const depuis = options.depuis ?? null;
  const jusqua = options.jusqua ?? null;

  const { messages } = decouper(texte);
  const lignes: LigneImportee[] = [];
  const anomalies: Anomalie[] = [];
  const auteurs = new Set<string>();
  const inconnus = new Map<string, number>();
  let messagesSansHeures = 0;

  for (const msg of messages) {
    if (depuis !== null && msg.jourEnvoi < depuis) continue;
    if (jusqua !== null && msg.jourEnvoi > jusqua) continue;

    const avant = lignes.length;
    lireMessage(msg, { salaries, equipeParAuteur }, lignes, anomalies, inconnus);
    if (lignes.length === avant) messagesSansHeures += 1;
    else auteurs.add(msg.auteur);
  }

  marquerDoublons(lignes);
  const nomsNonReconnus = [...inconnus.entries()]
    .map(([mot, occurrences]) => ({ mot, occurrences }))
    .sort((a, b) => b.occurrences - a.occurrences || a.mot.localeCompare(b.mot, 'fr'))
    .slice(0, INCONNUS_MAX);
  return {
    lignes,
    auteurs: [...auteurs].sort(),
    anomalies,
    nomsNonReconnus,
    messagesSansHeures,
  };
}

function lireMessage(
  msg: Message,
  ctx: { salaries: readonly Salarie[]; equipeParAuteur: Readonly<Record<string, string>> },
  lignes: LigneImportee[],
  anomalies: Anomalie[],
  inconnus: Map<string, number>,
): void {
  const { salaries } = ctx;
  const texteMessage = msg.lignes.join(' ');
  const [anneeEnvoi, moisEnvoi] = msg.jourEnvoi.split('-').map(Number);
  const ecrite = dateEcrite(texteMessage, anneeEnvoi ?? 0, moisEnvoi ?? 1);
  const jour = msg.jourEnvoi;
  const jourEcrit = ecrite !== null && ecrite !== jour ? ecrite : null;

  // Sans le mot « pause », il n'y a pas de pause déclarée — et surtout pas de
  // durée à aller chercher : sur « Joaquim 7h 16h », le 7 h serait pris pour
  // une pause de sept heures.
  const pauseAnnoncee = /\bpause\b/i.test(texteMessage);
  const pause = pauseAnnoncee ? parsePauseMinutes(texteMessage) : null;

  const equipe = ctx.equipeParAuteur[msg.auteur] ?? msg.auteur.split(/\s+/)[0] ?? msg.auteur;

  let libelle: string | null = null;
  let nomsEnAttente: NomTrouve[] = [];

  for (const ligne of msg.lignes) {
    libelle ??= libelleDeLaLigne(ligne, salaries);
    const heures = heuresDeLaLigne(ligne);

    if (heures.length === 0) {
      const noms = nomsDuSegment(ligne, salaries);
      if (noms.length > 0) nomsEnAttente = noms;
      continue;
    }

    // Les heures vont par deux. On découpe la ligne en tranches
    // « noms … début fin », ce qui permet à une même ligne de porter deux
    // groupes : « Justin 7h 14h Joaquim + Arnoux 7h 14h30 ».
    let curseur = 0;
    let paires = 0;
    for (let i = 0; i + 1 < heures.length; i += 2) {
      const debut = heures[i];
      const fin = heures[i + 1];
      if (!debut || !fin) break;
      paires += 1;

      const segment = ligne.slice(curseur, debut.debut);
      curseur = fin.fin;
      if (!ressembleAUnReleve(ligne, debut.minutes, fin.minutes)) continue;
      let noms = nomsDuSegment(segment, salaries);

      // « Vendredi 11 septembre 7h 13h30 sans pause Joaquim,Justin,Arnoux » :
      // les noms suivent l'horaire. On ne regarde derrière que pour le DERNIER
      // horaire de la ligne, sinon on volerait les noms du groupe suivant.
      if (noms.length === 0 && i + 2 >= heures.length) {
        noms = nomsDuSegment(ligne.slice(fin.fin), salaries);
        if (noms.length > 0) recenserInconnus(ligne.slice(fin.fin), salaries, inconnus);
      } else if (noms.length > 0) {
        recenserInconnus(segment, salaries, inconnus);
      }

      const retenus = noms.length > 0 ? noms : nomsEnAttente;
      if (noms.length > 0) nomsEnAttente = noms;

      if (retenus.length === 0) {
        anomalies.push({
          code: 'nom_inconnu',
          message: 'Des heures sans nom identifiable. À attribuer à la main.',
          source: ligne,
        });
        continue;
      }

      for (const nom of retenus) {
        const propres: Anomalie[] = [];
        if (nom.ambigu) {
          propres.push({
            code: 'nom_ambigu',
            message: `« ${nom.mot} » peut désigner plusieurs salariés.`,
            source: ligne,
          });
        }
        if (jourEcrit !== null) {
          propres.push({
            code: 'date_ecrite_differente',
            message: `Message envoyé le ${jour}, mais il annonce le ${jourEcrit}.`,
            source: ligne,
          });
        }
        if (pause === null && pauseAnnoncee) {
          propres.push({
            code: 'pause_sans_duree',
            message: 'Une pause est annoncée sans durée.',
            source: ligne,
          });
        }
        lignes.push({
          jour,
          jourEcrit,
          auteur: msg.auteur,
          equipe,
          nom: nom.salarie?.prenom ?? nom.mot,
          employeId: nom.salarie?.employeId ?? null,
          debut: debut.minutes,
          fin: fin.minutes,
          pauseDeclaree: pause,
          chantierLibelle: libelle,
          source: ligne,
          anomalies: propres,
        });
      }
    }

    // Une heure orpheline sur une ligne qui nomme quelqu'un : c'est une saisie
    // qu'on n'a pas su lire, pas une ligne sans intérêt.
    if (heures.length > paires * 2 && nomsDuSegment(ligne, salaries).length > 0) {
      anomalies.push({
        code: 'horaire_illisible',
        message: 'Une heure isolée, sans horaire de fin lisible.',
        source: ligne,
      });
    }
  }
}

/** Au-delà, la liste n'aide plus personne : c'est un collage de tout le groupe. */
const INCONNUS_MAX = 20;

/**
 * Les mots d'un segment qui ne sont ni une date, ni un mot outil, ni un nom
 * connu — relevés SEULEMENT là où un nom connu figure déjà, pour ne pas
 * ramasser la moitié du vocabulaire du groupe.
 */
function recenserInconnus(
  segment: string,
  salaries: readonly Salarie[],
  inconnus: Map<string, number>,
): void {
  for (const mot of segment.split(/[^\p{L}]+/u)) {
    if (mot.length < 3) continue;
    const n = normaliser(mot);
    if (MOTS_IGNORES.has(n)) continue;
    if (moisDuMot(mot) !== null) continue;
    const { salarie, ambigu } = reconnaitre(mot, salaries);
    if (salarie !== null || ambigu) continue;
    inconnus.set(mot, (inconnus.get(mot) ?? 0) + 1);
  }
}

/** Deux relevés pour la même personne le même jour : quelqu'un doit trancher. */
function marquerDoublons(lignes: LigneImportee[]): void {
  const par = new Map<string, LigneImportee[]>();
  for (const l of lignes) {
    const cle = `${l.jour}|${l.employeId ?? normaliser(l.nom)}`;
    par.set(cle, [...(par.get(cle) ?? []), l]);
  }
  for (const groupe of par.values()) {
    if (groupe.length < 2) continue;
    for (const l of groupe) {
      l.anomalies.push({
        code: 'doublon_jour',
        message: `${String(groupe.length)} relevés pour ${l.nom} le ${l.jour}. Un seul doit rester.`,
        source: l.source,
      });
    }
  }
}
